/**
 * Predictive Models → Propensity → Detail
 *
 * Single-page setup workflow for one propensity config. Sections:
 *
 *   1. Training data preview — Redshift mart schema, fill rates, sample rows.
 *   2. Risk segments editor (thresholds + recommended actions).
 *   3. Validation pass panel (run + display CV metrics with bootstrap CIs).
 *   4. Approve flow (AUC gate + warn-acknowledge checkbox).
 *
 * Why one page (vs. a wizard)
 * ---------------------------
 * Real admin behaviour is iterative — they tweak a segment, re-validate,
 * tweak a threshold, re-validate. Splitting these across wizard steps
 * forces lots of back-button work. Each section is collapsible.
 */
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import { useNavigate, useParams } from 'react-router-dom';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import { getTenantIdFromStorage } from '../../../services/tenantSettingsService';
import {
  getConfig,
  updateConfig,
  validateConfig,
  approveConfig,
  getTargetValues,
  ConfigDetail,
  SegmentRuleRead,
  TargetValueOption,
  ValidationStatus,
} from '../../../services/propensityService';
import MartPreviewPanel from '../components/MartPreviewPanel';
import FieldMappingPanel from '../components/FieldMappingPanel';
import { useCan } from '../../../ui/PermissionsProvider';


const VALIDATION_COLOR: Record<ValidationStatus, 'success' | 'warning' | 'error'> = {
  green: 'success',
  warn: 'warning',
  refuse: 'error',
};

// ---------------------------------------------------------------------------
// Segment rules editor
// ---------------------------------------------------------------------------

interface SegmentRulesProps {
  rules: SegmentRuleRead[];
  onChange: (rules: SegmentRuleRead[]) => void;
  canEdit: boolean;
}

function SegmentRulesEditor({ rules, onChange, canEdit }: SegmentRulesProps) {
  const update = (idx: number, patch: Partial<SegmentRuleRead>) => {
    onChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }} icon={<HelpOutlineRounded />}>
        <Typography variant="body2">
          Conditions use a small expression language: comparisons (
          <code>{'<, <=, ==, !=, >=, >'}</code>) joined by <code>AND / OR</code>,
          referencing engineered feature names. Example:
          <br />
          <code>premium_to_income_ratio &gt;= 0.15 AND inforce_months &lt; 24</code>
        </Typography>
      </Alert>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="20%">Segment</TableCell>
            <TableCell width="45%">Condition</TableCell>
            <TableCell width="35%">Recommended action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rules.map((r, idx) => (
            <TableRow key={idx}>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  disabled={!canEdit}
                  value={r.name}
                  onChange={(e) => update(idx, { name: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  disabled={!canEdit}
                  value={r.condition}
                  onChange={(e) => update(idx, { condition: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  disabled={!canEdit}
                  value={r.recommended_action}
                  onChange={(e) => update(idx, { recommended_action: e.target.value })}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Validation panel
// ---------------------------------------------------------------------------

function metric(value: number | undefined, decimals = 3): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return value.toFixed(decimals);
}

function ValidationPanel({ cfg }: { cfg: ConfigDetail }) {
  const m = cfg.validation_metrics;
  const status = cfg.validation_status;
  if (!m || !status) {
    return (
      <Alert severity="info">
        No validation results yet. Run validation to evaluate model performance.
      </Alert>
    );
  }
  const color = VALIDATION_COLOR[status];
  const headline = (
    <Stack direction="row" alignItems="center" spacing={1}>
      {status === 'green' ? <CheckCircleRounded color="success" /> : null}
      {status === 'warn' ? <WarningAmberRounded color="warning" /> : null}
      <Typography variant="h6">
        AUC <strong>{metric(m.auc_mean)}</strong>{' '}
        <Typography component="span" color="text.secondary" variant="body2">
          (95% CI {metric(m.auc_ci_low)} – {metric(m.auc_ci_high)})
        </Typography>
      </Typography>
      <Chip size="small" label={status.toUpperCase()} color={color} />
    </Stack>
  );
  // Diagnostic / rationale list. For REFUSE this carries the only useful
  // signal (e.g. "Only 1,508 rows available; need at least 5,000.") so we
  // surface it prominently rather than nesting it inside metrics.
  const reasonMessages = (m.messages || []).filter(Boolean);
  const reasonAlertSeverity =
    status === 'refuse' ? 'error' : status === 'warn' ? 'warning' : 'success';
  return (
    <Box>
      <Box sx={{ mb: 2 }}>{headline}</Box>
      {reasonMessages.length > 0 && (
        <Alert severity={reasonAlertSeverity} sx={{ mb: 2 }}>
          {reasonMessages.length === 1 ? (
            <Typography variant="body2">{reasonMessages[0]}</Typography>
          ) : (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {reasonMessages.map((msg, i) => (
                <li key={i}><Typography variant="body2">{msg}</Typography></li>
              ))}
            </Box>
          )}
        </Alert>
      )}
      <Stack direction="row" spacing={4}>
        <Box>
          <Typography variant="overline" color="text.secondary">Selected model</Typography>
          <Typography variant="body2">{cfg.selected_model_key || '—'}</Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary">Precision @ top decile</Typography>
          <Typography variant="body2">
            {metric(m.precision_at_top_decile_mean)}{' '}
            <span style={{ color: '#888' }}>
              ({metric(m.precision_at_top_decile_ci_low)}–{metric(m.precision_at_top_decile_ci_high)})
            </span>
          </Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary">Lift @ top decile</Typography>
          <Typography variant="body2">{metric(m.lift_at_top_decile_mean)}x</Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary">Brier score</Typography>
          <Typography variant="body2">{metric(m.brier_score)}</Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary">Training set</Typography>
          <Typography variant="body2">
            {m.n_train?.toLocaleString() || '—'} rows
            {m.positive_rate !== undefined &&
              ` · ${(m.positive_rate * 100).toFixed(1)}% positive`}
          </Typography>
        </Box>
      </Stack>

      {m.per_model && Object.keys(m.per_model).length > 1 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Candidate comparison
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Model</TableCell>
                <TableCell align="right">AUC (CI)</TableCell>
                <TableCell align="right">Precision@10%</TableCell>
                <TableCell align="right">Lift@10%</TableCell>
                <TableCell align="right">Brier</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(m.per_model).map(([key, mm]) => (
                <TableRow key={key} selected={key === cfg.selected_model_key}>
                  <TableCell>
                    {key}
                    {key === cfg.selected_model_key && (
                      <Chip size="small" label="selected" color="success" sx={{ ml: 1, height: 18 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {metric(mm.auc_mean)} ({metric(mm.auc_ci_low)}–{metric(mm.auc_ci_high)})
                  </TableCell>
                  <TableCell align="right">{metric(mm.precision_at_top_decile_mean)}</TableCell>
                  <TableCell align="right">{metric(mm.lift_at_top_decile_mean)}x</TableCell>
                  <TableCell align="right">{metric(mm.brier_score)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Target value picker
// ---------------------------------------------------------------------------
//
// Different tenants spell their terminal states differently (Lapse /
// Lapsed / Surrendered / TerminatedNonPay …) so the YAML's
// `target.positive_value` is only a default. This component reads the
// distinct values actually present in the tenant's mart and lets the
// admin pick which ones count as "event happened". The selection
// flows back through `PUT /configs/{id}` → `target_positive_value` and
// is what the validation runner / engine binarise against.
//
// Three visible states:
//   - Loading the distinct values (spinner)
//   - Mart unreachable / not yet loaded (empty state with a hint —
//     the backend returns 503 for both, the difference doesn't change
//     what the admin should do, which is "load data first")
//   - Loaded → list of distinct values + counts, multi-select chips

interface TargetValuePickerProps {
  tenantId: string;
  configId: string;
  targetField: string;
  initialPositiveWhen: 'values' | 'present' | 'absent';
  initialPositive: string[] | null;
  canEdit: boolean;
  onSaved: (cfg: ConfigDetail) => void;
}

const PRESENCE_LABELS: Record<string, string> = {
  __PRESENT__: 'Value present',
  __ABSENT__: 'Value absent',
};

function TargetValuePicker({
  tenantId,
  configId,
  targetField,
  initialPositiveWhen,
  initialPositive,
  canEdit,
  onSaved,
}: TargetValuePickerProps) {
  const { notify } = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<'values' | 'presence'>('values');
  const [options, setOptions] = React.useState<TargetValueOption[]>([]);
  const [truncated, setTruncated] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(
    new Set((initialPositive || []).map(String)),
  );
  const [presencePositive, setPresencePositive] = React.useState<
    'present' | 'absent'
  >(initialPositiveWhen === 'absent' ? 'absent' : 'present');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getTargetValues(tenantId, configId, { limit: 50 })
      .then((res) => {
        if (!alive) return;
        setMode(res.mode === 'presence' ? 'presence' : 'values');
        setOptions(res.values);
        setTruncated(res.truncated);
      })
      .catch((e: any) => {
        if (!alive) return;
        setError(e?.message || 'Could not list target values');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [tenantId, configId]);

  const initialKey = (initialPositive || []).map(String).join('|');
  React.useEffect(() => {
    setSelected(new Set((initialPositive || []).map(String)));
    setPresencePositive(initialPositiveWhen === 'absent' ? 'absent' : 'present');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey, initialPositiveWhen]);

  const toggle = (value: string | null) => {
    if (value === null) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'presence') {
        const updated = await updateConfig(tenantId, configId, {
          target_positive_when: presencePositive,
        });
        onSaved(updated);
        notify('Target definition saved', 'success');
        return;
      }
      if (selected.size === 0) {
        notify('Pick at least one positive value before saving.', 'warning');
        return;
      }
      const updated = await updateConfig(tenantId, configId, {
        target_positive_value: Array.from(selected),
        target_positive_when: 'values',
      });
      onSaved(updated);
      notify('Target value saved', 'success');
    } catch (e: any) {
      notify(e?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const initialSet = new Set((initialPositive || []).map(String));
  const isDirty = mode === 'presence'
    ? presencePositive !== initialPositiveWhen
    : (
      selected.size !== initialSet.size
      || Array.from(selected).some((v) => !initialSet.has(v))
    );

  const savedLabel = mode === 'presence'
    ? (initialPositiveWhen === 'present' ? 'Value present' : 'Value absent')
    : ((initialPositive && initialPositive.length > 0)
      ? initialPositive.join(', ')
      : '—');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', py: 2, gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Reading distinct values from <code>{targetField}</code>…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }} icon={<HelpOutlineRounded />}>
        <Typography variant="body2">
          {mode === 'presence' ? (
            <>
              Choose whether a <strong>populated</strong> or <strong>empty</strong>{' '}
              <code>{targetField}</code> counts as the positive class (event happened).
            </>
          ) : (
            <>
              Pick the values in <code>{targetField}</code> that count as
              {' '}<strong>event happened</strong>. Multi-select is supported when
              your taxonomy treats more than one terminal state as a positive
              (e.g. <code>Lapsed</code> + <code>Surrendered</code>).
            </>
          )}
        </Typography>
      </Alert>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{error}</Typography>
          <Typography variant="body2" color="text.secondary">
            This usually means the mart hasn&apos;t been loaded for this
            tenant yet. Load data into{' '}
            <code>{targetField}</code> and refresh.
          </Typography>
        </Alert>
      ) : (
        <>
          {options.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The target column is empty — load data into the mart, then
              return here to pick the positive values.
            </Alert>
          )}

          {options.length > 0 && mode === 'presence' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {options.map((opt) => {
                const bucket = opt.value === '__PRESENT__' ? 'present' : 'absent';
                const label = PRESENCE_LABELS[opt.value || ''] || opt.value || '(null)';
                const isSelected = presencePositive === bucket;
                return (
                  <Chip
                    key={label}
                    label={`${label} · ${opt.count.toLocaleString()}`}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={canEdit ? () => setPresencePositive(bucket as 'present' | 'absent') : undefined}
                    disabled={!canEdit}
                  />
                );
              })}
            </Box>
          )}

          {options.length > 0 && mode !== 'presence' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {options.map((opt) => {
                const label = opt.value === null ? '(null)' : opt.value;
                const isSelected = opt.value !== null && selected.has(opt.value);
                return (
                  <Chip
                    key={label}
                    label={`${label} · ${opt.count.toLocaleString()}`}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={canEdit && opt.value !== null
                      ? () => toggle(opt.value)
                      : undefined
                    }
                    disabled={!canEdit || opt.value === null}
                  />
                );
              })}
            </Box>
          )}

          {truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              More than 50 distinct values exist — only the most common are
              shown. If the value you need isn&apos;t here, that&apos;s
              usually a sign the wrong column was picked as the target.
            </Typography>
          )}
        </>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          size="small"
          disabled={!canEdit || saving || !isDirty || (mode !== 'presence' && selected.size === 0)}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save target value'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Currently saved: {savedLabel}
        </Typography>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export default function PropensityDetailPage() {
  const navigate = useNavigate();
  const { configId } = useParams<{ configId: string }>();
  const tenantId = React.useMemo(() => getTenantIdFromStorage(), []);
  const { notify } = React.useContext(SnackbarContext);
  const canEdit = useCan('manage_predictive_models');

  const [cfg, setCfg] = React.useState<ConfigDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [acknowledgeWarn, setAcknowledgeWarn] = React.useState(false);

  const [localSegments, setLocalSegments] = React.useState<SegmentRuleRead[]>([]);

  const refresh = React.useCallback(async () => {
    if (!tenantId || !configId) return;
    setLoading(true);
    try {
      const got = await getConfig(tenantId, configId);
      setCfg(got);
      setLocalSegments(got.payload.segments);
      setAcknowledgeWarn(got.warn_acknowledged);
    } catch (e: any) {
      notify(e?.message || 'Failed to load config', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, configId, notify]);

  React.useEffect(() => { void refresh(); }, [refresh]);

  const handleSave = React.useCallback(async () => {
    if (!tenantId || !configId) return;
    setBusy('save');
    try {
      const updated = await updateConfig(tenantId, configId, {
        segments: localSegments,
      });
      setCfg(updated);
      notify('Saved', 'success');
    } catch (e: any) {
      notify(e?.message || 'Save failed', 'error');
    } finally {
      setBusy(null);
    }
  }, [tenantId, configId, localSegments, notify]);

  const handleValidate = React.useCallback(async () => {
    if (!tenantId || !configId) return;
    setBusy('validate');
    try {
      // Save segments first so validation reflects the latest edits.
      await updateConfig(tenantId, configId, { segments: localSegments });
      const updated = await validateConfig(tenantId, configId, {});
      setCfg(updated);
      const status = updated.validation_status;
      notify(
        status === 'green'
          ? 'Validation passed — ready to approve'
          : status === 'warn'
            ? 'Validation passed with warnings'
            : 'Validation refused — see metrics',
        status === 'green' ? 'success' : status === 'warn' ? 'warning' : 'error',
      );
    } catch (e: any) {
      notify(e?.message || 'Validation failed', 'error');
    } finally {
      setBusy(null);
    }
  }, [tenantId, configId, localSegments, notify]);

  const handleApprove = React.useCallback(async () => {
    if (!tenantId || !configId) return;
    setBusy('approve');
    try {
      const updated = await approveConfig(tenantId, configId, acknowledgeWarn);
      setCfg(updated);
      notify('Approved — model is now available in chat', 'success');
    } catch (e: any) {
      notify(e?.message || 'Approve failed', 'error');
    } finally {
      setBusy(null);
    }
  }, [tenantId, configId, acknowledgeWarn, notify]);

  if (loading || !cfg) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const validationStatus = cfg.validation_status;
  const canApprove =
    cfg.status === 'validated'
    && validationStatus !== 'refuse'
    && (validationStatus === 'green' || acknowledgeWarn);
  const isApproved = cfg.status === 'approved';

  // Field mapping is informational for the demo — its status does NOT
  // gate the downstream steps. The accordion is always rendered so
  // admins can iterate on mappings at any time (including for legacy
  // configs that pre-date this UI, where `mapping_status` is null).
  // Kept as named constants instead of inlining so we can re-introduce
  // a gate later by flipping a single boolean.
  const mappingApproved = cfg.mapping_status === 'approved';
  const mappingSynced = cfg.last_mapping_sync_status === 'success';

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/predictive-models/propensity')}>
          <ArrowBackRounded fontSize="small" />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>{cfg.display_name}</Typography>
        <Chip label={cfg.status.toUpperCase()} color={isApproved ? 'success' : 'default'} />
      </Stack>

      {/* --- Field mapping ---------------------------------------------
       * Always rendered, regardless of `mapping_status`. The chip
       * surfaces the current state (legacy configs without a status
       * yet show "Not started"). Field-mapping work is informational
       * for the demo — it never blocks the downstream sections.
       */}
      <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
            <Typography variant="subtitle1">1. Field mapping &amp; sync</Typography>
            <Chip
              size="small"
              label={
                mappingApproved
                  ? (mappingSynced ? 'Synced' : 'Approved')
                  : cfg.mapping_status === 'pending' ? 'Needs review'
                    : 'Not started'
              }
              color={
                mappingSynced ? 'success'
                  : mappingApproved ? 'primary'
                    : cfg.mapping_status === 'pending' ? 'warning'
                      : 'default'
              }
              variant="outlined"
            />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {tenantId && configId && (
            <FieldMappingPanel
              tenantId={tenantId}
              configId={configId}
              canEdit={canEdit}
              onMappingChanged={(resp) => {
                setCfg((prev) => prev ? {
                  ...prev,
                  mapping_status: resp.mapping_status,
                  last_mapping_sync_status: resp.sync_status,
                  last_mapping_sync_at: resp.sync_completed_at,
                  field_mapping: resp.field_mapping,
                } : prev);
              }}
            />
          )}
        </AccordionDetails>
      </Accordion>

      {/* --- Mart preview ---------------------------------------------- */}
      <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          <Typography variant="subtitle1">2. Training data preview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {tenantId && configId && (
            <MartPreviewPanel tenantId={tenantId} configId={configId} />
          )}
        </AccordionDetails>
      </Accordion>

      {/* --- Target value ---------------------------------------------- */}
      <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          <Typography variant="subtitle1">
            3. Target value (&quot;{cfg.payload.target_field}&quot; ={' '}
            {cfg.payload.target_positive_when === 'present'
              ? 'value present'
              : cfg.payload.target_positive_when === 'absent'
                ? 'value absent'
                : (cfg.payload.target_positive_value && cfg.payload.target_positive_value.length > 0)
                  ? cfg.payload.target_positive_value.join(', ')
                  : '—'})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {tenantId && configId && (
            <TargetValuePicker
              tenantId={tenantId}
              configId={configId}
              targetField={cfg.payload.target_field}
              initialPositiveWhen={cfg.payload.target_positive_when || 'values'}
              initialPositive={cfg.payload.target_positive_value}
              canEdit={canEdit}
              onSaved={(updated) => setCfg(updated)}
            />
          )}
        </AccordionDetails>
      </Accordion>

      {/* --- Segments --------------------------------------------------- */}
      <Accordion sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          <Typography variant="subtitle1">
            4. Risk segments &amp; recommended actions
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SegmentRulesEditor
            rules={localSegments}
            onChange={setLocalSegments}
            canEdit={canEdit}
          />
        </AccordionDetails>
      </Accordion>

      {/* --- Validation ------------------------------------------------- */}
      <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
          <Typography variant="subtitle1">5. Validation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              disabled={!canEdit || busy === 'validate'}
              onClick={handleValidate}
            >
              {busy === 'validate' ? 'Running validation…' : 'Run validation'}
            </Button>
            <Button
              variant="outlined"
              disabled={!canEdit || busy === 'save'}
              onClick={handleSave}
            >
              Save changes
            </Button>
          </Stack>
          <ValidationPanel cfg={cfg} />
        </AccordionDetails>
      </Accordion>

      {/* --- Approve ---------------------------------------------------- */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            6. Approve &amp; deploy
          </Typography>
          <Divider sx={{ my: 1 }} />
          {validationStatus === 'refuse' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Validation refused this configuration. Adjust segments or
              the underlying training data and re-validate before approval.
            </Alert>
          )}
          {validationStatus === 'warn' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Validation passed with warnings — the model has limited
              predictive power on the current data. Acknowledge below to
              continue.
            </Alert>
          )}
          {validationStatus === 'green' && !isApproved && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Validation passed. Approving will register a Copilot tool that
              can run this model from chat.
            </Alert>
          )}
          {isApproved && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Approved on {cfg.approved_at ? new Date(cfg.approved_at).toLocaleString() : ''}.
              Available in chat.
            </Alert>
          )}
          {validationStatus === 'warn' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledgeWarn}
                  disabled={!canEdit || isApproved}
                  onChange={(_e, v) => setAcknowledgeWarn(v)}
                />
              }
              label="I understand the model has limited predictive power and want to deploy anyway."
            />
          )}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              disabled={
                !canApprove || !canEdit || busy === 'approve' || isApproved
              }
              onClick={handleApprove}
            >
              {isApproved ? 'Approved' : busy === 'approve' ? 'Approving…' : 'Approve'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
